/**
 * CourseCard — course card components for the Training Portal.
 *
 * Two variants (same props contract as before):
 *   EnrolledCourseCard  — My Learning tab (shows progress bar, status, continue/review button)
 *   CatalogCourseCard   — Catalogue tab (shows Mandatory/Elective badge, Enroll button)
 *
 * Visual redesign: thumbnail gradient area at top, richer metadata row,
 * cleaner button treatment. All existing props and logic preserved.
 *
 * EPIC-08-04 AC-2 / AC-3
 */

import { useTranslation } from 'react-i18next';
import type { Enrollment, CatalogCourse, ModuleType } from '../hooks/useEnrollments';
import { StarRating } from './StarRating';
import styles from './CourseCard.module.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODULE_GRADIENTS: Record<ModuleType, string> = {
  roleplay: 'linear-gradient(135deg, #0055a5 0%, #003d7a 100%)',
  quiz:     'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)',
  reading:  'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
  video:    'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
};

const MODULE_ICONS: Record<ModuleType, string> = {
  roleplay: '🎭',
  quiz:     '📝',
  reading:  '📖',
  video:    '🎬',
};

const LEVEL_LABEL: Record<string, string> = {
  L1: 'Beginner',
  L2: 'Intermediate',
  L3: 'Advanced',
  L4: 'Expert',
  L5: 'Master',
};

function isOverdue(dueDateIso: string | null): boolean {
  if (!dueDateIso) return false;
  return new Date(dueDateIso) < new Date();
}

// ---------------------------------------------------------------------------
// Thumbnail — colored gradient with module type icon
// ---------------------------------------------------------------------------

interface ThumbnailProps {
  moduleType: ModuleType;
  badge?: 'mandatory' | 'elective' | null;
  bannerImage?: string | null;
  title?: string;
}

function Thumbnail({ moduleType, badge, bannerImage, title }: ThumbnailProps): JSX.Element {
  const gradient = MODULE_GRADIENTS[moduleType] ?? MODULE_GRADIENTS.reading;
  const icon = MODULE_ICONS[moduleType] ?? '📚';

  if (bannerImage) {
    return (
      <div className={styles.thumbnail} style={{ background: gradient }}>
        <img className={styles.bannerImage} src={bannerImage} alt="" />
        {title && <span className={styles.bannerLabel}>{title}</span>}
        {badge && (
          <span
            className={`${styles.thumbnailBadge} ${
              badge === 'mandatory' ? styles.badgeMandatory : styles.badgeElective
            }`}
          >
            {badge === 'mandatory' ? 'Mandatory' : 'Elective'}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={styles.thumbnail} style={{ background: gradient }}>
      <span className={styles.thumbnailIcon} aria-hidden="true">{icon}</span>
      {badge && (
        <span
          className={`${styles.thumbnailBadge} ${
            badge === 'mandatory' ? styles.badgeMandatory : styles.badgeElective
          }`}
        >
          {badge === 'mandatory' ? 'Mandatory' : 'Elective'}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EnrolledCourseCard (My Learning)
// ---------------------------------------------------------------------------

interface EnrolledCardProps {
  enrollment: Enrollment;
  onContinue: (enrollmentId: string) => void;
  onOpenFeedback?: (courseId: string) => void;
}

export function EnrolledCourseCard({
  enrollment,
  onContinue,
  onOpenFeedback,
}: EnrolledCardProps): JSX.Element {
  const { t } = useTranslation('training');
  const overdue = isOverdue(enrollment.due_date);
  const languageLabel = (enrollment.language ?? 'en').toUpperCase();
  const progressPct =
    enrollment.modules_total > 0
      ? Math.round((enrollment.modules_completed / enrollment.modules_total) * 100)
      : 0;
  const isCompleted = enrollment.status === 'completed' || enrollment.status === 'failed';

  return (
    <article className={styles.card}>
      <Thumbnail
        moduleType={enrollment.module_type}
        badge={enrollment.is_mandatory ? 'mandatory' : null}
        bannerImage={enrollment.banner_image ?? null}
        title={enrollment.course_title}
      />

      <div className={styles.cardBody}>
        {/* Status chip */}
        <span className={`${styles.statusChip} ${styles[`status_${enrollment.status}`]}`}>
          {t(`learn.status.${enrollment.status}`)}
        </span>

        <h3 className={styles.cardTitle}>{enrollment.course_title}</h3>
        <p className={styles.cardDescription}>{enrollment.course_description}</p>

        {/* Module type + language meta */}
        <div className={styles.metaRow}>
          <span className={styles.metaItem}>
            {MODULE_ICONS[enrollment.module_type]} {t(`learn.moduleType.${enrollment.module_type}`)}
          </span>
          <span className={styles.metaItem}>{languageLabel}</span>
        </div>

        {(enrollment.average_rating ?? 0) > 0 && (
          <button
            type="button"
            className={styles.ratingRow}
            onClick={() => onOpenFeedback?.(enrollment.course_id)}
          >
            <StarRating value={enrollment.average_rating ?? 0} size="sm" />
            <span className={styles.ratingValue}>
              {(enrollment.average_rating ?? 0).toFixed(2)}/5
            </span>
            <span className={styles.ratingCount}>({enrollment.rating_count ?? 0})</span>
          </button>
        )}

        {/* Progress bar */}
        {enrollment.modules_total > 0 && (
          <div className={styles.progressBlock}>
            <div
              className={styles.progressBar}
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
            </div>
            <span className={styles.progressLabel}>
              {t('learn.progress', {
                completed: enrollment.modules_completed,
                total: enrollment.modules_total,
              })}
            </span>
          </div>
        )}

        {enrollment.due_date && (
          <p className={`${styles.dueDate} ${overdue ? styles.dueDateOverdue : ''}`}>
            {t(overdue ? 'learn.overdueLabel' : 'learn.dueLabel', {
              date: new Date(enrollment.due_date).toLocaleDateString(),
            })}
          </p>
        )}

        <button className={styles.actionButton} onClick={() => onContinue(enrollment.id)}>
          {isCompleted ? t('learn.reviewButton') : t('learn.continueButton')}
        </button>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// CatalogCourseCard (Catalogue)
// ---------------------------------------------------------------------------

interface CatalogCardProps {
  course: CatalogCourse;
  onEnroll: (courseId: string) => void;
  onContinue?: (enrollmentId: string) => void;
  isEnrolling: boolean;
  onOpenFeedback?: (courseId: string) => void;
}

export function CatalogCourseCard({
  course,
  onEnroll,
  onContinue,
  isEnrolling,
  onOpenFeedback,
}: CatalogCardProps): JSX.Element {
  const { t } = useTranslation('training');
  const languageLabel = (course.language ?? 'en').toUpperCase();
  const tags = Array.isArray(course.tags) ? course.tags : [];
  const hasEnrollment = Boolean(course.enrollment_id);
  const canContinue = hasEnrollment && Boolean(course.enrollment_id);
  const buttonLabel = canContinue
    ? (course.status === 'completed' ? t('learn.reviewButton') : t('learn.continueButton'))
    : t('learn.enrollButton');

  return (
    <article className={`${styles.card} ${styles.catalogCard}`}>
      <Thumbnail
        moduleType={course.module_type}
        badge={course.track === 'compliance' ? 'mandatory' : 'elective'}
        bannerImage={course.banner_image ?? null}
        title={course.title}
      />

      <div className={styles.cardBody}>
        <h3 className={styles.cardTitle}>{course.title}</h3>
        <p className={styles.cardDescription}>{course.description}</p>

        {/* Meta: type · level · language */}
        <div className={styles.metaRow}>
          <span className={styles.metaItem}>
            {MODULE_ICONS[course.module_type]} {t(`learn.moduleType.${course.module_type}`)}
          </span>
          {course.min_level && (
            <span className={styles.metaItem}>
              {LEVEL_LABEL[course.min_level] ?? course.min_level}
            </span>
          )}
          <span className={styles.metaItem}>{languageLabel}</span>
        </div>

        {(course.average_rating ?? 0) > 0 && (
          <button
            type="button"
            className={styles.ratingRow}
            onClick={() => onOpenFeedback?.(course.id)}
          >
            <StarRating value={course.average_rating ?? 0} size="sm" />
            <span className={styles.ratingValue}>
              {(course.average_rating ?? 0).toFixed(2)}/5
            </span>
            <span className={styles.ratingCount}>({course.rating_count ?? 0})</span>
          </button>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className={styles.tagRow}>
            {tags.slice(0, 3).map((tag, index) => (
              <span key={`${tag}-${index}`} className={styles.tag}>{tag}</span>
            ))}
          </div>
        )}

        <button
          className={styles.enrollButton}
          onClick={() => {
            if (canContinue && course.enrollment_id && onContinue) {
              onContinue(course.enrollment_id);
              return;
            }
            onEnroll(course.id);
          }}
          disabled={isEnrolling}
          aria-busy={isEnrolling}
        >
          {isEnrolling ? t('common.loading') : buttonLabel}
        </button>
      </div>
    </article>
  );
}
